import { getFile, removeFile, storeFile } from '../OPFSStore.js';
import { addQueueRecord, getJobIdsWithRecords, getQueueRecord, getQueueRecordsForJob, removeQueueRecord } from './UploadQueueStore.js';
import { Uploader } from '../../katapult-upload-page/Uploader.js';
import { getAuth } from 'firebase/auth';
import { KatapultJob } from '../../../modules/katapult-job/KatapultJob.js';

/** @import { AssociationConfig, PhotoUpdate, AssociatedPhotoUpdate, Metadata as PhotoMetadata, QueueRecord } from '../PhotoUpload/UploadQueueStore.js' */

/** Thrown when a photo's format isn't supported for upload.*/
export class UnsupportedPhotoFormatError extends Error {
  /** @param {string} [fileType] - The unsupported MIME type */
  constructor(fileType) {
    super(`Unsupported photo format "${fileType || 'unknown'}". Only JPEG images are supported for upload.`);
    this.name = 'UnsupportedPhotoFormatError';
  }
}

const firebaseAppId = globalThis.config.appName;
const firebaseAppConfig = globalThis.config.firebaseConfigs[firebaseAppId];
const isMobile = globalThis.isMobile === true;

// Report initial progress for any records in the queue.
await refreshPhotoUploadProgress();

let uploader;
/**
 * @returns {Uploader}
 */
function getUploaderInstance() {
  return (uploader ??= new Uploader(firebaseAppConfig, { isMobile, useWorker: true, parallelCount: 1 }));
}

const uploadEventCallbacks = {};

/** @typedef {Object} UploadProgress
 * @property {number} numPhotos -  Total number of photos being uploaded.
 * @property {number} numPhotosRemaining - Number of photos that have not yet been uploaded.
 * @property {number} numPhotosUploaded - Number of photos that have been fully uploaded.
 * @property {number} numPhotosFailed - Number of photos that have failed to upload.
 * @property {number} progress - Overall upload progress percentage (0-100).
 * @property {boolean} [complete] - Whether the upload is complete.
 * @property {?number} [completedAt] - Timestamp when the upload was completed, or null if not complete.
 * @property {?number} [incompleteAt] - Timestamp when the upload was marked incomplete (e.g. due to a new photo being added), or null if completed.
 * @property {boolean} [failed] - Whether the upload has failed.
 * @property {Record<string, { progress: number, complete: boolean, failed: boolean, metadata: QueueRecord['metadata'] }>} uploadProgressByRecord - Progress info for individual records.
 */

/** @type {Record<string, UploadProgress & { uploadProgressByNode?: Record<string, UploadProgress>, uploadProgressBySection?: Record<string, UploadProgress> }>} */
const uploadProgressByJob = {};

/**
 * Generates a unique key for a section based on connectionId and sectionId.
 * @param {string} connectionId
 * @param {string} sectionId
 * @returns {string}
 */
function getSectionKey(connectionId, sectionId) {
  return `${connectionId}:${sectionId}`;
}

/**
 * Retrieves upload progress info for a specific job.
 * @param {string} jobId
 * @returns {UploadProgress}
 */
function getUploadInfoForJob(jobId) {
  const jobUploadInfo = uploadProgressByJob[jobId];
  if (jobUploadInfo) return structuredClone(jobUploadInfo);
  return getEmptyUploadProgress();
}

/**
 * Retrieves upload progress info for a specific node within a job.
 * @param {string} jobId
 * @param {string} nodeId
 * @returns {UploadProgress}
 */
function getUploadInfoForNode(jobId, nodeId) {
  const nodeUploadInfo = uploadProgressByJob[jobId]?.uploadProgressByNode?.[nodeId];
  if (nodeUploadInfo) return structuredClone(nodeUploadInfo);
  return getEmptyUploadProgress();
}

/**
 * Retrieves upload progress info for a specific section within a job.
 * @param {string} jobId
 * @param {string} connectionId
 * @param {string} sectionId
 * @returns {UploadProgress}
 */
function getUploadInfoForSection(jobId, connectionId, sectionId) {
  const sectionKey = getSectionKey(connectionId, sectionId);
  const sectionUploadInfo = uploadProgressByJob[jobId]?.uploadProgressBySection?.[sectionKey];
  if (sectionUploadInfo) return structuredClone(sectionUploadInfo);
  return getEmptyUploadProgress();
}

/**
 * Creates an upload event listener based on the provided configuration.
 * @param {Object} listenerConfig
 * @param {string} listenerConfig.jobId
 * @param {string} [listenerConfig.nodeId]
 * @param {string} [listenerConfig.connectionId]
 * @param {string} [listenerConfig.sectionId]
 * @param {(UploadProgress) => void} listenerConfig.callback
 * @returns {Function} A function to disconnect the listener.
 */
function createUploadEventListener(listenerConfig) {
  const callbackInstanceId = crypto.randomUUID();
  uploadEventCallbacks[callbackInstanceId] = listenerConfig;
  const disconnect = () => delete uploadEventCallbacks[callbackInstanceId];
  return disconnect;
}

/**
 * Listens to upload events for a specific job and invokes the callback with progress updates.
 * @param {string} jobId
 * @param {(UploadProgress) => void} callback
 * @returns {Function} A function to disconnect the listener.
 */
function listenToUploadEventsForJob(jobId, callback) {
  const disconnect = createUploadEventListener({ jobId, callback });
  const jobUploadInfo = getUploadInfoForJob(jobId);
  callback(jobUploadInfo);
  return disconnect;
}

/**
 * Listens to upload events for a specific node within a job and invokes the callback with progress updates.
 * @param {string} jobId
 * @param {string} nodeId
 * @param {(UploadProgress) => void} callback
 * @returns {Function} A function to disconnect the listener.
 */
function listenToUploadEventsForNode(jobId, nodeId, callback) {
  const disconnect = createUploadEventListener({ jobId, nodeId, callback });
  const nodeUploadInfo = getUploadInfoForNode(jobId, nodeId);
  callback(nodeUploadInfo);
  return disconnect;
}

/**
 * Listens to upload events for a specific section within a job and invokes the callback with progress updates.
 * @param {string} jobId
 * @param {string} connectionId
 * @param {string} sectionId
 * @param {(UploadProgress) => void} callback
 * @returns {Function} A function to disconnect the listener.
 */
function listenToUploadEventsForSection(jobId, connectionId, sectionId, callback) {
  const disconnect = createUploadEventListener({ jobId, connectionId, sectionId, callback });
  const sectionUploadInfo = getUploadInfoForSection(jobId, connectionId, sectionId);
  callback(sectionUploadInfo);
  return disconnect;
}

/**
 * Notifies all progress listeners relevant to the given recordId of the current upload progress.
 * @param {string} recordId
 * @returns {Promise<void>}
 */
async function notifyProgressListeners(recordId) {
  for (const listenerConfig of Object.values(uploadEventCallbacks)) {
    const record = await getQueueRecord(recordId);
    if (!record) continue;

    // Notify job listeners.
    if (listenerConfig.jobId === record.jobId && !listenerConfig.nodeId && !listenerConfig.connectionId && !listenerConfig.sectionId) {
      const jobUploadInfo = getUploadInfoForJob(record.jobId);
      listenerConfig.callback(jobUploadInfo);
    }

    // Notify node listeners.
    if (
      listenerConfig.jobId === record.jobId &&
      listenerConfig.nodeId &&
      record.association &&
      'nodeId' in record.association &&
      listenerConfig.nodeId === record.association.nodeId
    ) {
      const nodeUploadInfo = getUploadInfoForNode(record.jobId, listenerConfig.nodeId);
      listenerConfig.callback(nodeUploadInfo);
    }

    // Notify section listeners.
    if (
      listenerConfig.jobId === record.jobId &&
      listenerConfig.connectionId &&
      listenerConfig.sectionId &&
      record.association &&
      'connectionId' in record.association &&
      'sectionId' in record.association &&
      listenerConfig.connectionId === record.association.connectionId &&
      listenerConfig.sectionId === record.association.sectionId
    ) {
      const sectionUploadInfo = getUploadInfoForSection(record.jobId, listenerConfig.connectionId, listenerConfig.sectionId);
      listenerConfig.callback(sectionUploadInfo);
    }
  }
}

/**
 * @typedef {Object} AddPhotoToQueueOptions
 * @property {AssociationConfig} [association] - Configuration for associating the photo after upload.
 * @property {?AssociatedPhotoUpdate} [associatedPhotoUpdate]
 * @property {?PhotoUpdate} [photoUpdate]
 * @property {?PhotoMetadata} [metadata] - Additional metadata to store with the photo upload.
 */

/**
 * Adds a photo to the upload queue.
 * @param {File} file
 * @param {string} jobId
 * @param {AddPhotoToQueueOptions} [options]
 * @returns {Promise<string>} The id of the added queue record.
 */
async function addPhotoToQueue(file, jobId, options) {
  if (!file) throw new Error('file is required to add a photo to the upload queue.');
  if (!jobId) throw new Error('jobId is required to add a photo to the upload queue.');

  const currentUserUid = getAuth().currentUser?.uid;
  if (!currentUserUid) throw new Error('User must be authenticated to add a photo to the upload queue.');

  // Generate a unique photo id for this upload so that retries can reference the same photo.
  const photoId = crypto.randomUUID();
  const queueRecord = {
    jobId,
    photoId,
    filetype: file.type,
    filename: file.name,
    uploadedBy: currentUserUid,
    metadata: options?.metadata,
    association: options?.association,
    associatedPhotoUpdate: options?.associatedPhotoUpdate,
    photoUpdate: options?.photoUpdate
  };

  const recordId = await addQueueRecord(queueRecord);
  await storeFile('photo_uploads', recordId, file).catch(async (error) => {
    // If storing the photo data fails, remove the queue record to avoid bad records.
    await removeQueueRecord(recordId);
    throw error;
  });

  return recordId;
}

/**
 * Removes a photo from the upload queue and deletes its stored data.
 * @param {string} recordId
 * @param {Object} [options]
 * @param {boolean} [options.notifyRemoval=false] - Determines whether the removal should be reflected in the progress
 * @returns {Promise<void>}
 */
async function removePhotoFromQueue(recordId, options) {
  const notifyRemoval = options?.notifyRemoval ?? false;
  if (notifyRemoval) await notifyPhotoUploadProgress(recordId, { removed: true });
  await removeFile('photo_uploads', recordId);
  await removeQueueRecord(recordId);
}

/**
 * Creates an empty UploadProgress object.
 * @returns {UploadProgress}
 */
function getEmptyUploadProgress() {
  return {
    numPhotos: 0,
    numPhotosRemaining: 0,
    numPhotosUploaded: 0,
    numPhotosFailed: 0,
    progress: 0,
    complete: true,
    completedAt: Date.now(),
    incompleteAt: null,
    failed: false,
    uploadProgressByRecord: {}
  };
}

/**
 * Sets the progress for a specific record within the given progress info.
 * @param {UploadProgress} uploadProgress
 * @param {QueueRecord} record
 * @param {{ progress?: number, complete?: boolean, failed?: boolean, removed?: boolean }} newRecordProgress
 */
function setRecordProgressAndUpdateProgress(uploadProgress, record, newRecordProgress) {
  uploadProgress.uploadProgressByRecord ??= {};

  // If this upload progress is already complete (and has been for 1 second to avoid UI flicker), remove any progress info for completed records.
  if (uploadProgress.complete && uploadProgress.completedAt != null && Date.now() - uploadProgress.completedAt > 1000) {
    for (const otherRecordId in uploadProgress.uploadProgressByRecord) {
      const recordProgress = uploadProgress.uploadProgressByRecord[otherRecordId];
      if (recordProgress.complete) {
        delete uploadProgress.uploadProgressByRecord[otherRecordId];
      }
    }
  }

  // TODO (01/29/2026): Make this work a bit better
  if (newRecordProgress.removed) delete uploadProgress.uploadProgressByRecord[record.id];
  else {
    uploadProgress.uploadProgressByRecord[record.id] ??= {
      progress: 0,
      complete: false,
      failed: false,
      metadata: record.metadata,
      createdAt: record.createdAt
    };
    Object.assign(uploadProgress.uploadProgressByRecord[record.id], newRecordProgress);
  }

  const recordProgressArray = Object.values(uploadProgress.uploadProgressByRecord);
  const numPhotos = recordProgressArray.length;
  const numPhotosRemaining = recordProgressArray.filter((p) => !p.complete && !p.failed).length;
  const numPhotosUploaded = recordProgressArray.filter((p) => p.complete).length;
  const numPhotosFailed = recordProgressArray.filter((p) => p.failed).length;
  const recordProgressSum = recordProgressArray.filter((p) => !p.failed).reduce((sum, p) => sum + p.progress, 0);
  const progress = numPhotos - numPhotosFailed === 0 ? 0 : recordProgressSum / (numPhotos - numPhotosFailed);
  const complete = numPhotosRemaining == 0;

  const recordsWithCreatedAt = recordProgressArray.filter((p) => p.createdAt != null);
  // Get the timestamp of the most recent completed photo.
  const completedAt =
    recordsWithCreatedAt
      .filter((p) => p.complete)
      .map((p) => p.createdAt)
      .sort((a, b) => b - a)[0] ?? null;
  // Get the timestamp of the oldest incomplete photo.
  const incompleteAt =
    recordsWithCreatedAt
      .filter((p) => !p.complete)
      .map((p) => p.createdAt)
      .sort((a, b) => a - b)[0] ?? null;

  const failed = numPhotosFailed > 0;
  Object.assign(uploadProgress, {
    numPhotos,
    numPhotosRemaining,
    numPhotosUploaded,
    numPhotosFailed,
    progress,
    complete,
    completedAt,
    incompleteAt,
    failed
  });
}

/**
 * Manages and notifies photo upload progress by:
 * - Updating progress info structures.
 * - Notifying relevant listeners of progress updates.
 * - Cleaning up progress info structures when uploads complete.
 * @param {string} recordId
 * @param {{ progress?: number, complete?: boolean, failed?: boolean, removed?: boolean }} progress
 * @returns {Promise<void>}
 */
async function notifyPhotoUploadProgress(recordId, progress) {
  const record = await getQueueRecord(recordId);
  if (!record) return;

  // Track progress by job.
  const jobUploadInfo = (uploadProgressByJob[record.jobId] ??= getEmptyUploadProgress());
  // Reset job upload info if it is already complete.
  setRecordProgressAndUpdateProgress(jobUploadInfo, record, progress);

  // Track progress by node.
  if (record.association && 'nodeId' in record.association) {
    jobUploadInfo.uploadProgressByNode ??= {};
    const nodeUploadInfo = (jobUploadInfo.uploadProgressByNode[record.association.nodeId] ??= getEmptyUploadProgress());
    // Reset node upload info if it is already complete.
    setRecordProgressAndUpdateProgress(nodeUploadInfo, record, progress);
  }

  // Track progress by section.
  if (record.association && 'connectionId' in record.association && 'sectionId' in record.association) {
    jobUploadInfo.uploadProgressBySection ??= {};
    const sectionKey = getSectionKey(record.association.connectionId, record.association.sectionId);
    const sectionUploadInfo = (jobUploadInfo.uploadProgressBySection[sectionKey] ??= getEmptyUploadProgress());
    // Reset section upload info if it is already complete.
    setRecordProgressAndUpdateProgress(sectionUploadInfo, record, progress);
  }

  await notifyProgressListeners(recordId);
}

/**
 * Rebuilds the upload progress info for all jobs based on the current state of the upload queue, and notifies listeners of the current progress.
 */
export async function refreshPhotoUploadProgress() {
  // TODO: Does not handle partial progress of photos that no longer appear in the queue (e.g. was uploaded in another tab)
  const jobIds = await getJobIdsWithRecords();
  jobIds.forEach(async (jobId) => {
    const records = await getQueueRecordsForJob(jobId);
    records.forEach((record) => {
      notifyPhotoUploadProgress(record.id, { progress: 0 });
      uploadPhotoFromQueue(record.id);
    });
  });
}

/**
 * Uploads (and optionally associates) a photo from the upload queue by adding it to an `Uploader` instance's internal upload queue.
 * Upon successful upload, the photo record is removed from the upload queue store and its stored data is deleted.
 * @param {string} recordId
 * @returns {Promise<string>} The id of the uploaded photo.
 */
async function uploadPhotoFromQueue(recordId) {
  const photoRecord = await getQueueRecord(recordId);
  if (!photoRecord) throw new Error(`No queued photo found with id ${recordId}`);

  const photoData = await getFile('photo_uploads', recordId);
  if (!photoData) throw new Error(`No photo data found for queued photo with id ${recordId}`);

  // Create a promise that resolves/rejects when the upload completes/fails.
  // This is done to "promisify" the callback-based `Uploader.uploadFromFilesArray()` method.
  let resolveUploadComplete;
  let rejectUploadComplete;
  const uploadComplete = new Promise((resolve, reject) => {
    resolveUploadComplete = resolve;
    rejectUploadComplete = reject;
  });

  notifyPhotoUploadProgress(recordId, { progress: 0 });

  // Pass the photo id beside the file because a File loses added properties when posted to the upload worker
  const photoFile = new File([photoData], photoRecord.filename, { type: photoRecord.filetype });
  const filesArray = [{ file: photoFile, _photoId: photoRecord.photoId ?? recordId }];

  // TODO: Allow folderId and cameraId to be specified in the queue record.
  const uploadOptions = { jobId: photoRecord.jobId, folderId: 'mobile_upload', cameraId: 'default', uid: photoRecord.uploadedBy };
  const uploader = getUploaderInstance();
  uploader.uploadFromFilesArray(filesArray, uploadOptions, async (message) => {
    try {
      switch (message.status) {
        case 'failed': {
          notifyPhotoUploadProgress(recordId, { failed: true });
          rejectUploadComplete(new Error(message.message ?? 'Photo upload failed'));
          break;
        }
        case 'uploading': {
          notifyPhotoUploadProgress(recordId, { progress: message.progress });
          break;
        }
        case 'skipped':
        case 'complete': {
          const photoId = message.photo?._key;

          if (photoRecord.association) {
            const jobRef = KatapultJob.fromId(photoRecord.jobId);

            // Associate the photo to a node.
            if ('nodeId' in photoRecord.association) {
              const { nodeId } = photoRecord.association;
              await jobRef.associatePhotoToNode(nodeId, photoId, true);

              // Apply any associated photo updates to the photo record on the node.
              if (photoRecord.associatedPhotoUpdate) {
                const nodePhotoRef = globalThis.FirebaseWorker.ref(
                  `photoheight/jobs/${photoRecord.jobId}/nodes/${nodeId}/photos/${photoId}`
                );
                await nodePhotoRef.update(photoRecord.associatedPhotoUpdate);
              }
            }

            // Associate the photo to a section.
            if ('connectionId' in photoRecord.association && 'sectionId' in photoRecord.association) {
              const { connectionId, sectionId } = photoRecord.association;
              await jobRef.associatePhotoToSection(sectionId, connectionId, photoId, true);

              // Apply any associated photo updates to the photo record on the section.
              if (photoRecord.associatedPhotoUpdate) {
                const sectionPhotoRef = globalThis.FirebaseWorker.ref(
                  `photoheight/jobs/${photoRecord.jobId}/connections/${connectionId}/sections/${sectionId}/photos/${photoId}`
                );
                await sectionPhotoRef.update(photoRecord.associatedPhotoUpdate);
              }
            }
          }

          // Apply any photo updates to the main photo record.
          if (photoRecord.photoUpdate) {
            const photoRef = globalThis.FirebaseWorker.ref(`photoheight/jobs/${photoRecord.jobId}/photos/${photoId}`);
            await photoRef.update(photoRecord.photoUpdate);
          }

          // Log completed upload progress and notify listeners of the progress change.
          notifyPhotoUploadProgress(recordId, { complete: true, progress: 100 });
          // Resolve the upload promise.
          resolveUploadComplete(photoId);
          // Once the upload is complete and listeners are notified, remove the queue record and stored photo data.
          await removePhotoFromQueue(recordId);
        }
      }
    } catch (error) {
      rejectUploadComplete(error);
    }
  });

  return uploadComplete;
}

/**
 * @typedef {AddPhotoToQueueOptions} UploadOptions
 */

/**
 * Adds a photo to the upload queue and request it be uploaded (if offline, upload will be deferred).
 * @param {File} file - The photo data to be uploaded.
 * @param {string} jobId - The id of the job to which the photo should be uploaded.
 * @param {UploadOptions} [options] - Additional upload options.
 * @returns {Promise<string>} The id of the uploaded photo.
 */
async function uploadPhoto(file, jobId, options) {
  if (file.type !== 'image/jpeg') {
    throw new UnsupportedPhotoFormatError(file.type);
  }

  // If this file is named "image.jpg" (the default name iOS gives to photos), give it a unique name.
  // Otherwise this will result in multiple photos having the same name which is an issue for GetExif() which builds
  // out a name_date string  based on the file name and uses it to determine if the photo already has a key in firebase.
  if (file.name === 'image.jpg') {
    file = new File([file], crypto.randomUUID() + '.jpg', { type: file.type, lastModified: file.lastModified });
  }

  const recordId = await addPhotoToQueue(file, jobId, options);
  const photoId = await uploadPhotoFromQueue(recordId);
  return photoId;
}

/**
 * Uploads all queued photos for a specific job.
 * @param {string} jobId
 * @returns {Promise<void>}
 */
async function uploadQueuedJobPhotos(jobId) {
  const jobPhotoRecordIds = (await getQueueRecordsForJob(jobId)).map((record) => record.id);
  const uploadPromises = jobPhotoRecordIds.map((recordId) => uploadPhotoFromQueue(recordId));
  await Promise.all(uploadPromises);
}

export {
  uploadPhoto,
  uploadQueuedJobPhotos,
  listenToUploadEventsForJob,
  listenToUploadEventsForNode,
  listenToUploadEventsForSection,
  removePhotoFromQueue
};
